import {Component, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ToasterService} from 'angular2-toaster';
import {DataService} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {KobeacService} from './kobeac.service';
import {TranslocoService} from '@jsverse/transloco';

interface NotificationRow {
    rid: string;
    notifyDate: number;
    category: string;
    subject: string;
    content: string;
    targetAll: boolean;
    active: boolean;
    customerRids: string[];
    customerLabels: string[];
}

/**
 * kobe-ac 通知管理 (管理者)。神戸オークション管理者が全顧客または指定顧客へ通知を作成・編集・削除する。
 * 顧客ユーザは自分宛て (全顧客 or 指定に自分が含まれる) の通知をダッシュボードで見る。
 */
@Component({
    standalone: false,
    selector: 'kobeac-notification',
    templateUrl: 'notification.component.html',
})
export class KobeacNotificationComponent implements OnInit {
    private base = ActivePromotionConfig.SERVER_URL + '/rest/kobeac/notification';

    list: NotificationRow[] = [];
    loading = false;
    saving = false;

    /** カテゴリ未登録時のフォールバック候補。AC_Master(category='通知区分') があればそちらを優先。 */
    private readonly DEFAULT_CATEGORIES = ['お知らせ', '重要', 'メンテナンス', 'イベント'];
    /** カテゴリ選択肢。AC_Master(category='通知区分') の value を sort 順で読む。 */
    categories: string[] = this.DEFAULT_CATEGORIES.slice();

    /** 本文 (HTML) の WYSIWYG エディタ設定。news-management と同設定。 */
    tinymceConfig: any = {
        height: 320,
        menubar: false,
        plugins:
            'advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table code help wordcount',
        toolbar:
            'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link | removeformat | code | help',
        language: 'ja',
        language_url: 'assets/tinymce/langs/ja.js',
    };

    /** 編集中の通知。rid が空なら新規。 */
    editing: { rid: string; category: string; subject: string; content: string; targetAll: boolean; active: boolean } | null = null;
    /** 顧客指定時に選択した顧客 @rid 集合。 */
    selected: { [rid: string]: boolean } = {};
    customerFilter = '';

    customers: any[] = [];

    constructor(private http: HttpClient, private dataService: DataService,
                private kobeac: KobeacService, private toaster: ToasterService, private transloco: TranslocoService) {
    }

    async ngOnInit(): Promise<void> {
        await this.kobeac.ensureLoaded();
        this.customers = this.kobeac.customers;
        this.loadCategories();
        this.load();
    }

    /** カテゴリ候補を AC_Master(category='通知区分') から読む。無ければ既定候補のまま。 */
    private loadCategories(): void {
        const sql = "SELECT value FROM AC_Master WHERE category = '通知区分' ORDER BY sort";
        this.dataService.query({sql: sql, count: sql, fetchPlan: '*:0'}, ActivePromotionConfig.SERVER_URL).subscribe({
            next: (res: any) => {
                const vals: string[] = [];
                if (res && Array.isArray(res.results)) {
                    for (const r of res.results) {
                        const v = r && r.value != null ? String(r.value) : '';
                        if (v && vals.indexOf(v) < 0) {
                            vals.push(v);
                        }
                    }
                }
                if (vals.length) {
                    this.categories = vals;
                    if (this.editing && this.editing.category && vals.indexOf(this.editing.category) < 0) {
                        this.categories = [this.editing.category].concat(vals);
                    }
                }
            },
            error: () => { /* 既定候補のまま */ },
        });
    }

    /** 編集中カテゴリが候補に無い場合も選べるよう合成した候補。 */
    get categoryOptions(): string[] {
        const cur = this.editing && this.editing.category ? this.editing.category : '';
        return cur && this.categories.indexOf(cur) < 0 ? [cur].concat(this.categories) : this.categories;
    }

    formatDateTime(millis: number): string {
        if (!millis || millis <= 0) {
            return '';
        }
        const d = new Date(millis);
        const p = (n: number) => (n < 10 ? '0' + n : '' + n);
        return d.getFullYear() + '/' + p(d.getMonth() + 1) + '/' + p(d.getDate())
            + ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
    }

    load(): void {
        this.loading = true;
        this.http.get(this.base + '/list').subscribe({
            next: (res: any) => {
                this.list = (res && res.items) ? res.items : [];
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.toaster.pop('error', this.transloco.translate('kobeac.notification'), this.transloco.translate('kobeac.list_load_failed'));
            },
        });
    }

    /** 対象表示ラベル。 */
    targetLabel(n: NotificationRow): string {
        if (n.targetAll) {
            return this.transloco.translate('kobeac.all_customers');
        }
        const n2 = n.customerLabels ? n.customerLabels.length : 0;
        return this.transloco.translate('kobeac.specified_customers_n', {count: n2});
    }

    newItem(): void {
        const defCat = this.categories.length ? this.categories[0] : 'お知らせ';
        this.editing = {rid: '', category: defCat, subject: '', content: '', targetAll: true, active: true};
        this.selected = {};
        this.customerFilter = '';
    }

    edit(n: NotificationRow): void {
        this.editing = {rid: n.rid, category: n.category || 'お知らせ', subject: n.subject, content: n.content,
            targetAll: n.targetAll, active: n.active !== false};
        this.selected = {};
        for (const rid of (n.customerRids || [])) {
            this.selected[rid] = true;
        }
        this.customerFilter = '';
    }

    cancel(): void {
        this.editing = null;
    }

    /** フィルタに一致する顧客 (先頭200件まで、絞り込み前提)。 */
    get filteredCustomers(): any[] {
        const f = this.customerFilter.trim();
        const arr = !f ? this.customers
            : this.customers.filter((c) => (String(c['名称'] || '').indexOf(f) >= 0) || (String(c['ID'] || '').indexOf(f) >= 0));
        return arr.slice(0, 200);
    }

    get selectedCount(): number {
        return Object.keys(this.selected).filter((k) => this.selected[k]).length;
    }

    toggle(rid: string): void {
        this.selected[rid] = !this.selected[rid];
    }

    save(): void {
        if (!this.editing) {
            return;
        }
        const e = this.editing;
        if (!e.subject.trim()) {
            this.toaster.pop('warning', this.transloco.translate('kobeac.notification'), this.transloco.translate('kobeac.enter_subject'));
            return;
        }
        const rids = Object.keys(this.selected).filter((k) => this.selected[k]);
        if (!e.targetAll && rids.length === 0) {
            this.toaster.pop('warning', this.transloco.translate('kobeac.notification'), this.transloco.translate('kobeac.select_target'));
            return;
        }
        this.saving = true;
        const body: any = {
            category: (e.category || '').trim() || 'お知らせ',
            subject: e.subject, content: e.content, targetAll: e.targetAll, active: e.active !== false,
            customerRids: e.targetAll ? [] : rids,
        };
        if (e.rid) {
            body.rid = e.rid;
        }
        this.http.post(this.base + '/save', body).subscribe({
            next: () => {
                this.saving = false;
                this.toaster.pop('info', this.transloco.translate('kobeac.notification'), this.transloco.translate('common.saved'));
                this.editing = null;
                this.load();
            },
            error: () => {
                this.saving = false;
                this.toaster.pop('error', this.transloco.translate('kobeac.notification'), this.transloco.translate('kobeac.save_failed'));
            },
        });
    }

    remove(n: NotificationRow): void {
        this.http.post(this.base + '/delete', {rid: n.rid}).subscribe({
            next: () => {
                this.toaster.pop('info', this.transloco.translate('kobeac.notification'), this.transloco.translate('common.deleted'));
                if (this.editing && this.editing.rid === n.rid) {
                    this.editing = null;
                }
                this.load();
            },
            error: () => this.toaster.pop('error', this.transloco.translate('kobeac.notification'), this.transloco.translate('kobeac.delete_failed')),
        });
    }
}
